import {Slider} from '@wandb/weave/components';
import {debounce} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';

import {DD_PRIVACY_ALLOW} from '../../../../util/datadog/privacy';

export const SettingSlider = ({
  dataTest,
  name,
  min,
  max,
  step,
  sliderValue,
  updateSetting,
  ddActionName,
}: {
  dataTest?: string;
  name: string;
  min: number;
  max: number;
  step: number;
  sliderValue: number;
  updateSetting: (newVal: number) => void;
  // Stable Datadog RUM action name covering the slider and its numeric stepper.
  ddActionName?: string;
}) => {
  const [currValue, setCurrValue] = useState(sliderValue);
  const lastValRef = useRef(sliderValue);
  const relativePositionRef = useRef(
    max > min ? (sliderValue - min) / (max - min) : 0
  );

  useEffect(() => {
    if (lastValRef.current === sliderValue) {
      return;
    }

    lastValRef.current = sliderValue;
    const val = min + relativePositionRef.current * (max - min);
    const snapped = min + Math.round((val - min) / step) * step;
    setCurrValue(Math.max(min, Math.min(snapped, max)));
  }, [sliderValue, min, max, step]);

  const debouncedUpdateSetting = debounce(updateSetting, 300);

  return (
    <div
      className="flex w-full items-center gap-8"
      data-test={dataTest}
      data-dd-action-name={ddActionName}
      data-dd-privacy={DD_PRIVACY_ALLOW}>
      <div className="flex-1">
        <Slider.Root
          name={name}
          min={min}
          max={max}
          step={step}
          orientation="horizontal"
          onValueChange={([newVal]) => {
            // If use is dragging slider, don't update config continuosly
            lastValRef.current = newVal;
            relativePositionRef.current =
              max > min ? (newVal - min) / (max - min) : 0;
            setCurrValue(newVal);
          }}
          onValueCommit={([newVal]) => {
            // Once user lets go of slider, we'll update config
            debouncedUpdateSetting(newVal);
          }}
          value={[currValue]}>
          <Slider.Track>
            <Slider.Range className="grow" />
          </Slider.Track>
          <Slider.Thumb />
        </Slider.Root>
      </div>
      <Slider.Display
        isDirty={true}
        min={min}
        max={max}
        step={step}
        value={currValue}
        onChange={newVal => {
          lastValRef.current = newVal;
          relativePositionRef.current =
            max > min ? (newVal - min) / (max - min) : 0;
          setCurrValue(newVal);
        }}
        onBlur={newVal => {
          const clampedVal = Math.max(min, Math.min(newVal, max));
          lastValRef.current = clampedVal;
          relativePositionRef.current =
            max > min ? (clampedVal - min) / (max - min) : 0;
          setCurrValue(clampedVal);
          updateSetting(clampedVal);
        }}
      />
    </div>
  );
};
